import * as React from 'react';

import {
  Badge,
  Button,
  DataGrid,
  FloatingToolbar,
  ListScreenLayout,
  Pagination,
  type PaginationProps,
  SearchField,
  useMemoDataGridRows,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideX } from 'lucide-react';

import * as state from '../../_state/containersList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { tradeRoutePaths as tradeRoutes } from '../../routes';
import {
  ContainerRecord,
  ContainerSortOption,
  enumContainerSortOption,
} from '../../types/container';

import { ContainerVoidDialog } from './ContainerVoidDialog';
import { useContainersListColumns } from './hooks/useContainersListColumns';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: ContainerSortOption;
    desc: ContainerSortOption;
  }
> = {
  status: {
    asc: enumContainerSortOption.StatusAsc,
    desc: enumContainerSortOption.StatusDesc,
  },
  createdAt: {
    asc: enumContainerSortOption.CreatedAtAsc,
    desc: enumContainerSortOption.CreatedAtDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ CONTAINERS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const ContainersListView = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  // Atoms
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [searchTerm, setSearchTerm] = useAtom(state.$searchTerm);
  const [selectedContainer, setSelectedContainer] = useAtom(
    state.$selectedContainer,
  );
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useAtom(
    state.$isVoidDialogOpen,
  );
  const setCurrentSorting = useSetAtom(state.$currentSorting);
  const {
    data,
    isFetching,
    loadingState,
    error,
    refetch: refetchContainers,
  } = useAtomValue(state.$remoteContainers);
  const { refetch: refetchStatusCounts } = useAtomValue(
    state.$containerStatusCounts,
  );
  const voidContainer = useSetAtom(state.$voidContainer);

  const toast = useToast();

  const containers = data?.containers ?? [];
  const total = data?.totalCount ?? 0;

  // Callbacks
  const handleRefetch = React.useCallback(() => {
    refetchContainers();
    refetchStatusCounts();
  }, [refetchContainers, refetchStatusCounts]);
  const handlePaginationChange = React.useCallback<
    NonNullable<PaginationProps['onChange']>
  >(
    ({ limit: newLimit, offset: newOffset }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleContainerClick = React.useCallback(
    (container: ContainerRecord) => {
      navigate(tradeRoutes.containerDetail, {
        params: { id: container.id },
        keepSearchParams: false,
      });
    },
    [navigate],
  );
  const handleVoidContainer = React.useCallback(
    (containerId: string) => {
      setSelectedContainer({ id: containerId } as ContainerRecord);
      setIsVoidDialogOpen(true);
    },
    [setSelectedContainer, setIsVoidDialogOpen],
  );
  const handleVoidConfirm = React.useCallback(
    async (containerId: string) => {
      try {
        await voidContainer({ id: containerId });
        toast.success(t.containersView.messages.voided);
        setSelectedContainer(null);
        setIsVoidDialogOpen(false);
        handleRefetch();
      } catch (err) {
        toast.error(
          t.containersView.messages.voidError(
            err instanceof Error ? err.message : t.common.unknown,
          ),
        );
      }
    },
    [
      voidContainer,
      t,
      toast,
      setSelectedContainer,
      setIsVoidDialogOpen,
      handleRefetch,
    ],
  );
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumContainerSortOption.CreatedAtDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );
  const handleSearchChange = React.useCallback(
    (value: string) => {
      setSearchTerm(value || '');
      setOffset(0);
    },
    [setSearchTerm, setOffset],
  );

  // Other data hooks
  const { columns } = useContainersListColumns({
    onDoubleClick: handleContainerClick,
    onVoid: handleVoidContainer,
    onRefetch: handleRefetch,
  });
  const selectedRows = useMemoDataGridRows(containers, selectedKeys);

  const handleVoidSelected = React.useCallback(async () => {
    try {
      await Promise.all(
        selectedRows.map((row) => voidContainer({ id: row.id })),
      );
      toast.success(
        t.containersView.messages.voidedMultiple(selectedRows.length),
      );
      setSelectedKeys(new Set());
      handleRefetch();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t.containersView.messages.error,
      );
    }
  }, [selectedRows, voidContainer, t, toast, setSelectedKeys, handleRefetch]);

  const emptyPanelProps = {
    title: t.containersView.emptyState.title,
    description: <span>{t.containersView.emptyState.subtitle}</span>,
    centered: true,
  };
  const emptyResultsPanelProps = {
    title: t.containersView.emptyResultsState.title,
    description: t.containersView.emptyResultsState.subtitle,
    centered: true,
  };
  const hasFiltersActive = Boolean(searchTerm);

  return (
    <div className="@ContainersListView flex h-full flex-col">
      <ListScreenLayout
        filters={
          <SearchField
            autoFocus
            value={searchTerm ?? ''}
            onChange={handleSearchChange}
            placeholder={t.containersView.filters.search.placeholder}
            className="w-64"
          />
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
        className="@ContainersListView"
      >
        <DataGrid<ContainerRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={containers}
          rowKey="id"
          aria-label={t.containersView.table.aria}
          selectionMode="multiple"
          onSelectionChange={setSelectedKeys}
          onColumnSort={handleColumnSort}
          className="w-full text-sm"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.containersView.content.error(error.message),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={
            hasFiltersActive ? emptyResultsPanelProps : emptyPanelProps
          }
        />
      </ListScreenLayout>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="flex flex-row items-center gap-2 text-sm">
            <Badge color="primary" size="small">
              {selectedRows.length}
            </Badge>{' '}
            {t.containersView.toolbar.selected}
          </div>
          <Button
            variant="outline"
            isDisabled={selectedRows.length < 1}
            onPress={handleVoidSelected}
          >
            {t.containersView.toolbar.void(selectedRows.length)}
          </Button>
          <LucideX
            className="size-4 cursor-pointer text-neutral/80"
            onClick={() => setSelectedKeys(new Set())}
          />
        </div>
      </FloatingToolbar>

      {selectedContainer && isVoidDialogOpen && (
        <ContainerVoidDialog
          containerId={selectedContainer.id}
          onVoid={handleVoidConfirm}
          onCancel={() => {
            setSelectedContainer(null);
            setIsVoidDialogOpen(false);
          }}
        />
      )}
    </div>
  );
};
